import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Field, Input } from "@/components/ui";
import { apiErrorText, readApiError, readApiErrorResponse } from "@/lib/api-error";
import { makeBackgroundInert, wrapTabFocus } from "@/lib/modal-focus";
import { platformFetch } from "@/services/http/platform-fetch";

type Settings = { termsVersion: string; termsState: "draft" | "approved"; aiTutorEnabled: boolean };
type SaveResult = Settings & {
  changed: boolean;
  coursesQueued?: number;
  coursesToSweep?: number;
  queueFailed?: boolean;
};

const settingsKey = (tenantId: string) => ["platform-school-settings", tenantId] as const;
const path = (tenantId: string) =>
  `/platform/admin/tenants/${encodeURIComponent(tenantId)}/settings`;

async function fetchSettings(tenantId: string): Promise<Settings> {
  const res = await platformFetch(path(tenantId));
  if (!res.ok) throw await readApiErrorResponse(res);
  return ((await res.json()) as { data: Settings }).data;
}

async function putSettings(tenantId: string, body: Settings): Promise<SaveResult> {
  const res = await platformFetch(path(tenantId), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    // The route takes exactly these three fields (strict), never the whole view.
    body: JSON.stringify({
      termsVersion: body.termsVersion,
      termsState: body.termsState,
      aiTutorEnabled: body.aiTutorEnabled,
    }),
  });
  if (!res.ok) throw await readApiErrorResponse(res);
  return ((await res.json()) as { data: SaveResult }).data;
}

function ConfirmDialog(props: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const titleId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const restore = rootRef.current ? makeBackgroundInert(rootRef.current) : () => {};
    cancelRef.current?.focus();
    return () => {
      restore();
      opener?.focus?.();
    };
  }, []);
  return createPortal(
    <div
      ref={rootRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            props.onCancel();
            return;
          }
          wrapTabFocus(e, rootRef.current);
        }}
        className="w-full max-w-md rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-5 text-[var(--text-primary)] shadow-lg"
      >
        <p id={titleId} className="font-medium">
          {props.title}
        </p>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">{props.body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={props.onCancel}
            className="rounded border px-4 py-2 text-sm hover:bg-[var(--surface-3)]"
          >
            {props.cancelLabel}
          </button>
          <button
            type="button"
            onClick={props.onConfirm}
            className="rounded bg-[var(--ink-9)] px-4 py-2 text-sm font-medium text-[var(--text-inverse)]"
          >
            {props.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * MT-1 Plan 6 S7 (TEN-006, FR-11 UI): the platform admin sets a school's terms version and state
 * and its AI tutor switch. A terms change asks first, naming the school; enabling the tutor
 * reports how many courses were queued for indexing.
 */
export function SchoolSettingsCard({
  tenantId,
  schoolName,
}: {
  tenantId: string;
  schoolName: string;
}) {
  const t = useTranslations("platform.schoolSettings");
  const tRoot = useTranslations();
  const client = useQueryClient();
  const query = useQuery({
    queryKey: settingsKey(tenantId),
    queryFn: () => fetchSettings(tenantId),
  });
  const [form, setForm] = useState<Settings | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState<{ saved: boolean; queue: string | null } | null>(null);
  const [versionError, setVersionError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const radioName = useId();
  const switchId = useId();

  useEffect(() => {
    if (query.data) setForm({ ...query.data });
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: Settings) => putSettings(tenantId, body),
    onSuccess: (result) => {
      const saved: Settings = {
        termsVersion: result.termsVersion,
        termsState: result.termsState,
        aiTutorEnabled: result.aiTutorEnabled,
      };
      client.setQueryData(settingsKey(tenantId), saved);
      let queue: string | null = null;
      if (result.queueFailed) queue = t("queueFailed");
      else if (result.coursesQueued !== undefined) {
        queue = result.coursesToSweep
          ? t("queuedMore", { queued: result.coursesQueued, toSweep: result.coursesToSweep })
          : t("queued", { queued: result.coursesQueued });
      }
      setMessage({ saved: true, queue });
    },
    onError: (err) => {
      setMessage(null);
      const issues = readApiError(err)?.issues ?? [];
      if (issues.some((i) => i.path === "termsVersion")) {
        setVersionError(t("termsVersionInvalid"));
        return;
      }
      setFormError(apiErrorText(err, tRoot, "platform.schoolSettings.saveFailed"));
    },
  });

  if (query.isPending)
    return <p className="mt-2 text-sm text-[var(--text-muted)]">{t("loading")}</p>;
  if (query.isError || !form || !query.data) {
    return (
      <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
        {t("loadFailed")}
      </p>
    );
  }

  const saved = query.data;
  const termsChanged =
    form.termsVersion !== saved.termsVersion || form.termsState !== saved.termsState;
  const dirty = termsChanged || form.aiTutorEnabled !== saved.aiTutorEnabled;

  function save() {
    setConfirming(false);
    setMessage(null);
    setVersionError(null);
    setFormError(null);
    mutation.mutate(form!);
  }

  const stateLabel = form.termsState === "approved" ? t("stateApproved") : t("stateDraft");

  return (
    <section
      className="mt-6 rounded-lg border border-[var(--border-strong)] p-4"
      aria-labelledby="school-settings-title"
      data-testid="school-settings"
    >
      <h2 id="school-settings-title" className="text-lg font-semibold">
        {t("title")}
      </h2>
      <form
        className="mt-3 flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (termsChanged) setConfirming(true);
          else save();
        }}
      >
        <Field label={t("termsVersionLabel")} hint={t("termsVersionHint")} error={versionError}>
          <Input
            value={form.termsVersion}
            onChange={(e) => setForm({ ...form, termsVersion: e.target.value })}
            maxLength={64}
          />
        </Field>
        <fieldset className="flex gap-4">
          <legend className="mb-1 text-sm font-medium">{t("termsStateLabel")}</legend>
          {(["draft", "approved"] as const).map((state) => (
            <label key={state} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name={radioName}
                checked={form.termsState === state}
                onChange={() => setForm({ ...form, termsState: state })}
              />
              {state === "approved" ? t("stateApproved") : t("stateDraft")}
            </label>
          ))}
        </fieldset>
        <div className="flex items-center gap-3 text-sm">
          <input
            id={switchId}
            type="checkbox"
            role="switch"
            checked={form.aiTutorEnabled}
            onChange={(e) => setForm({ ...form, aiTutorEnabled: e.target.checked })}
          />
          <label htmlFor={switchId} className="font-medium">
            {t("tutorLabel")}
          </label>
          <span className="text-[var(--text-muted)]">
            {form.aiTutorEnabled ? t("tutorOn") : t("tutorOff")}
          </span>
        </div>
        {formError && (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {formError}
          </p>
        )}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={!dirty || mutation.isPending}
            className="h-10 rounded-md bg-[var(--ink-9)] px-4 text-sm font-medium text-[var(--text-inverse)] disabled:opacity-60"
          >
            {mutation.isPending ? t("saving") : t("save")}
          </button>
          {message?.saved && (
            <p role="status" className="text-sm">
              {t("saved")}
            </p>
          )}
        </div>
        {message?.queue && (
          <p role="status" data-testid="school-settings-queue" className="text-sm">
            {message.queue}
          </p>
        )}
      </form>
      {confirming && (
        <ConfirmDialog
          title={t("confirmTitle", { school: schoolName })}
          body={t("confirmBody", {
            school: schoolName,
            version: form.termsVersion,
            state: stateLabel,
          })}
          confirmLabel={t("confirm")}
          cancelLabel={t("cancel")}
          onConfirm={save}
          onCancel={() => setConfirming(false)}
        />
      )}
    </section>
  );
}
